/**
 * Teacher Juno's reactions (WOW · feel): a tiny bus any code can poke (`junoReact`), fed
 * automatically from the app's event stream (lib/analytics onAppEvent):
 *   step done → quick cheer · great photo → wow + Scribble cheer · weak photo → kind "try again" ·
 *   lesson finished → proud + voice line · very first drawing → big wow · back after days away → hello.
 * Voice lines are only spoken when nobody else is talking (the lesson voice-over always wins).
 */
import { create } from 'zustand';
import { daysAway, type PathId } from '@sketchling/core';
import { appNow, useApp } from '../state/store';
import type { JunoFace, ScribbleMood } from '../art/subjects/poses';
import { onAppEvent } from './analytics';

export type JunoReactionKind = 'stepDone' | 'greatCapture' | 'retry' | 'lessonDone' | 'firstDrawing' | 'comeback' | 'hello';

export interface JunoReaction {
  id: number;
  kind: JunoReactionKind;
  face: JunoFace;
  scribble?: ScribbleMood;
  /** Variant index of the line (i18n key `fx.j.<kind>.<n>`). */
  variant: number;
  vars: Record<string, string | number>;
  /** Speak the line (when no one else is talking). */
  voice: boolean;
  /** Milliseconds on screen. */
  ms: number;
  world?: PathId;
}

export const REACTION_STYLE: Record<JunoReactionKind, { face: JunoFace; scribble?: ScribbleMood; variants: number; voice: boolean; ms: number }> = {
  stepDone: { face: 'cheer', variants: 4, voice: false, ms: 1900 },
  greatCapture: { face: 'wow', scribble: 'cheer', variants: 3, voice: false, ms: 2600 },
  retry: { face: 'thinking', scribble: 'curious', variants: 3, voice: false, ms: 2800 },
  lessonDone: { face: 'proud', scribble: 'proud', variants: 3, voice: true, ms: 3400 },
  firstDrawing: { face: 'laugh', scribble: 'cheer', variants: 2, voice: true, ms: 4000 },
  comeback: { face: 'laugh', scribble: 'wave', variants: 3, voice: true, ms: 4200 },
  hello: { face: 'smile', scribble: 'wave', variants: 2, voice: true, ms: 3200 },
};

interface JunoBus {
  current: JunoReaction | null;
  /** Rotates variants so kids don't hear the same line twice in a row. */
  counters: Partial<Record<JunoReactionKind, number>>;
  show(r: JunoReaction): void;
  clear(id?: number): void;
}

export const useJunoBus = create<JunoBus>((set, get) => ({
  current: null,
  counters: {},
  show: (r) => set({ current: r }),
  clear: (id) => {
    if (id === undefined || get().current?.id === id) set({ current: null });
  },
}));

let seq = 0;
let lastAt = 0;

export function junoReact(kind: JunoReactionKind, vars: Record<string, string | number> = {}, opts: { world?: PathId } = {}): void {
  const st = useApp.getState();
  if (st.config.junoReactions === false) return;
  // Don't let a quick step cheer interrupt a bigger moment that is still on screen.
  const cur = useJunoBus.getState().current;
  const now = Date.now();
  if (cur && kind === 'stepDone' && cur.kind !== 'stepDone' && now - lastAt < cur.ms) return;
  lastAt = now;
  const style = REACTION_STYLE[kind];
  const n = useJunoBus.getState().counters[kind] ?? Math.floor(Math.random() * style.variants);
  useJunoBus.setState((s) => ({ counters: { ...s.counters, [kind]: n + 1 } }));
  const profile = st.profiles.find((p) => p.id === st.activeProfileId);
  useJunoBus.getState().show({
    id: ++seq,
    kind,
    face: style.face,
    scribble: style.scribble,
    variant: (n % style.variants) + 1,
    vars: { name: profile?.name ?? '', ...vars },
    voice: style.voice,
    ms: style.ms,
    world: opts.world,
  });
}

/** Feed reactions from the app's event stream. Returns an unsubscribe. */
export function startJunoReactions(): () => void {
  const off = onAppEvent((name, props) => {
    switch (name) {
      case 'Video Step Ready':
        if (!props.last) junoReact('stepDone', { n: Number(props.chapter ?? 0) + 1 });
        break;
      case 'Drawing Feedback Received':
        junoReact(Number(props.stars ?? 0) >= 2 ? 'greatCapture' : 'retry');
        break;
      case 'Unit Completed': {
        const st = useApp.getState();
        const p = st.activeProfileId ? st.progress[st.activeProfileId] : undefined;
        junoReact((p?.completedMiniUnits.length ?? 0) <= 1 && (p?.drawingsCount ?? 0) <= 1 ? 'firstDrawing' : 'lessonDone');
        break;
      }
    }
  });
  return off;
}

const COMEBACK_KEY = 'sk-juno-comeback';

/** Once per launch and profile: greet a child who comes back after 2+ days away. */
export function maybeGreetComeback(): boolean {
  const st = useApp.getState();
  const id = st.activeProfileId;
  if (!id || !st.onboarding.done) return false;
  const p = st.progress[id];
  if (!p) return false;
  const away = daysAway(p.activeDays, appNow());
  if (away === null || away < 2) return false;
  try {
    const k = `${id}:${p.activeDays[p.activeDays.length - 1] ?? ''}`;
    if (sessionStorage.getItem(COMEBACK_KEY) === k) return false;
    sessionStorage.setItem(COMEBACK_KEY, k);
  } catch {
    /* ignore */
  }
  junoReact('comeback', { days: away });
  return true;
}
